import React, { useEffect, Fragment } from 'react';
import { Button, Modal, ModalBody, ModalFooter, Form, } from 'reactstrap';
import { mapValues, omit, keys, } from 'lodash';
import { useToggle, useList, } from 'react-use';
import { toast, } from 'react-toastify';

import useFormState from '../hooks/useFormState';
import Field from '../Field';
import ListForm from '../ListForm';
import ProductSelectOptionLabel from '../ProductSelectOptionLabel';
import { fields, conditionFields, actionFields, conditionOperators, } from '../../shared/models/macros';

const { entries, } = Object;

export default function MacroFormModal(props) {
  const { isOpen, values = {}, products = [], onClickClose, } = props;
  const isNew = !values || !values.id;
  const productOptions = products.map((product) => {
    const { id, sku, code, asin, } = product;
    return {
      value: id,
      queryLabel: [id, sku, code, asin].join(' '),
      label: (
        <ProductSelectOptionLabel product={product} />
      ),
    };
  });
  const statedFields = useFormState(values, fields(), isOpen);
  const [conditions, { set: setConditions, updateAt: updateConditionAt }] = useList(
    values.conditions || [{ fieldType: 'product', operator: 'contains', productId: null }]
  );
  const [actions, { set: setActions, updateAt: updateActionAt }] = useList(
    values.actions || [{ type: 'setStatusPending' }]
  );
  const [isSubmitting, toggleSubmitting] = useToggle(false);
  const isUnsubmittable =
    Object.values(statedFields).some(_ => !_.isValid) ||
    conditions.length === 0 ||
    conditions.some(_ => !_.isValid) ||
    actions.length === 0 ||
    actions.some(_ => !_.isValid);
  const onSubmit = async (event) => {
    event.preventDefault();
    if(isUnsubmittable) return;

    toggleSubmitting(true);
    try {
      await props.onSubmit({
        ...mapValues(statedFields, 'value'),
        conditions: conditions.map(_ => omit(_, 'isValid')),
        actions: actions.map(_ => omit(_, 'isValid')),
      });
    } catch(e) {
      toast.error(`エラーが発生しました (${e.message})`, { autoClose: false });
      throw e;
    } finally {
      toggleSubmitting(false);
    }
  };

  return (
    <Modal className="position-relative" isOpen={isOpen} style={{ minWidth: 800 }}>
      <div className="modal-header">
        <h5 className="modal-title">
          マクロ{isNew ? '追加' : '編集'}
        </h5>
      </div>
      <Form onSubmit={onSubmit}>
        <ModalBody className="overflow-auto" style={{ maxHeight: '70vh' }}>
          <div>
            {
              entries(statedFields).map(([fieldName, fieldSetting]) => (
                <Field
                  key={fieldName}
                  name={fieldName}
                  {...fieldSetting}
                />
              ))
            }
          </div>
          <hr className="my-4" />
          <div className="mb-4">
            <h5>条件</h5>
            <div className="text-muted small mb-2">
              全ての条件に一致した場合にアクションを実行します
            </div>
            <ListForm
              initialItem={_ => ({ fieldType: 'product', operator: 'contains', productId: null })}
              items={conditions}
              renderItem={(condition, i) => (
                <div className="card p-3">
                  <ConditionForm
                    fields={conditionFields({ products, condition })}
                    index={i}
                    values={condition}
                    productOptions={productOptions}
                    onChange={_ => updateConditionAt(i, {
                      ...condition,
                      ..._,
                      ...(_.fieldType !== condition.fieldType && { operator: keys(conditionOperators[_.fieldType])[0] }),
                    })}
                  />
                </div>
              )}
              onChange={setConditions}
              minItems={1}
            />
          </div>
          <hr className="my-4" />
          <div>
            <h5>アクション</h5>
            <ListForm
              initialItem={_ => ({ type: 'setStatusPending' })}
              items={actions}
              renderItem={(action, i) => (
                <div className="card p-3">
                  <ActionForm
                    fields={actionFields()}
                    index={i}
                    values={action}
                    onChange={_ => updateActionAt(i, { ...action, ..._ })}
                  />
                </div>
              )}
              onChange={setActions}
              minItems={1}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button className="cancel" color="secondary" onClick={onClickClose}>閉じる</Button>
          <Button className="save" type="submit" color="primary" onClick={onSubmit} disabled={isUnsubmittable || isSubmitting}>保存</Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
}

function ConditionForm (props) {
  const { fields, values, onChange, productOptions, } = props;
  const statedFields = useFormState(values, fields, values);
  useEffect(() => {
    onChange({
      ...mapValues(statedFields, 'value'),
      isValid: Object.values(statedFields).every(_ => _.isValid),
    });
  }, [...Object.values(statedFields).map(_ => _.value), ...Object.values(statedFields).map(_ => _.isValid)]);

  return (
    <div className="d-flex flex-wrap gap-2">
      {
        entries(statedFields).map(([fieldName, fieldSetting]) => (
          <div key={fieldName} className="mr-2" style={{ minWidth: ['productId', 'postalCodes'].includes(fieldName) ? 500 : 180 }}>
            <Field
              name={fieldName}
              {...fieldSetting}
              {...(fieldName === 'productId' && {
                options: productOptions,
                inputProps: {
                  styles: {
                    valueContainer: _ => ({ ..._, height: 60 }),
                    singleValue: _ => ({ ..._, top: 0, transform: '' }),
                  },
                },
              })}
            />
          </div>
        ))
      }
    </div>
  );
}

function ActionForm (props) {
  const { fields, values, onChange, } = props;
  const statedFields = useFormState(values, fields, values);
  useEffect(() => {
    onChange({
      ...mapValues(statedFields, 'value'),
      isValid: Object.values(statedFields).every(_ => _.isValid),
    });
  }, [...Object.values(statedFields).map(_ => _.value), ...Object.values(statedFields).map(_ => _.isValid)]);

  return (
    <div className="d-flex flex-wrap gap-2">
      {
        entries(statedFields).map(([fieldName, fieldSetting]) => (
          <div key={fieldName} className="mr-2" style={{ minWidth: 240 }}>
            <Field
              name={fieldName}
              {...fieldSetting}
            />
          </div>
        ))
      }
    </div>
  );
}
